import React from "react";
import { useNavigate } from "react-router-dom";
import { useGetCampaignsQuery, useGetHomeQuery } from "@/store/rest_api";
import { DialerSeat } from "@/enum/DialerSeat";
import { MembershipRole } from "@/enum/MembershipRole";
import { MembershipStatus } from "@/enum/MembershipStatus";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Zap, Megaphone, Plus, Loader2, Pencil, Radio, BarChart3 } from "lucide-react";
import type Campaign from "@/model/interface/Campaign";
import { CampaignStatus, DialingMode } from "@/model/interface/Campaign";

const statusVariants: Record<CampaignStatus, "default" | "secondary" | "outline" | "destructive"> = {
  [CampaignStatus.DRAFT]: "secondary",
  [CampaignStatus.ACTIVE]: "default",
  [CampaignStatus.PAUSED]: "outline",
  [CampaignStatus.COMPLETED]: "secondary",
  [CampaignStatus.ARCHIVED]: "secondary",
};

const statusLabels: Record<CampaignStatus, string> = {
  [CampaignStatus.DRAFT]: "Draft",
  [CampaignStatus.ACTIVE]: "Active",
  [CampaignStatus.PAUSED]: "Paused",
  [CampaignStatus.COMPLETED]: "Completed",
  [CampaignStatus.ARCHIVED]: "Archived",
};

const dialingModeLabels: Record<DialingMode, string> = {
  [DialingMode.PREVIEW]: "Preview",
  [DialingMode.POWER]: "Power",
  [DialingMode.PREDICTIVE]: "Predictive",
  [DialingMode.PROGRESSIVE]: "Progressive",
  [DialingMode.AI_AGENT]: "AI Agent",
};

export default function CampaignSelectScreen() {
  const navigate = useNavigate();
  const { data: campaigns, isLoading, error } = useGetCampaignsQuery();
  const { data: user } = useGetHomeQuery();

  const launchableCampaigns =
    campaigns?.filter((c) => c.status !== CampaignStatus.COMPLETED && c.status !== CampaignStatus.ARCHIVED) ?? [];
  const membership = user?.activeMembership;
  const hasSeat = Boolean(membership?.dialerSeat && membership.dialerSeat !== DialerSeat.NONE);
  const canAssignSeats =
    membership?.status === MembershipStatus.ACTIVE &&
    (membership.role === MembershipRole.OWNER || membership.role === MembershipRole.ADMIN);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <p className="text-sm text-destructive">Failed to load campaigns</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Zap className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Power Dialer</h1>
            <p className="text-muted-foreground">Select a campaign to start dialing</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/power-dialer/reports")}>
            <BarChart3 className="mr-2 h-4 w-4" />
            Reports
          </Button>
          <Button variant="outline" onClick={() => navigate("/power-dialer/monitor")}>
            <Radio className="mr-2 h-4 w-4" />
            Live Monitor
          </Button>
        </div>
      </div>

      {user && !hasSeat && (
        <Card>
          <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-medium">You don't have a power dialer seat yet</p>
              <p className="text-sm text-muted-foreground">
                {canAssignSeats
                  ? "Only members with a seat can start dialing. Assign yourself one on the Add-ons page."
                  : "Only members with a seat can start dialing. Ask an owner or admin to assign you one on the Add-ons page."}
              </p>
            </div>
            {canAssignSeats && (
              <Button variant="outline" onClick={() => navigate("/manage-organisation/billing/add-ons")}>
                Assign seats
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {launchableCampaigns.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Megaphone className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No campaigns available</h3>
            <p className="text-muted-foreground text-center mb-4 max-w-md">
              Create a campaign with contacts to start using the power dialer.
            </p>
            <Button onClick={() => navigate("/campaigns/new")}>
              <Plus className="mr-2 h-4 w-4" />
              Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Your Campaigns</CardTitle>
            <CardDescription>
              {launchableCampaigns.length} {launchableCampaigns.length === 1 ? "campaign" : "campaigns"} available
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {launchableCampaigns.map((campaign: Campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell>
                      <div>
                        <button
                          className="font-medium underline-offset-4 hover:underline"
                          onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}
                        >
                          {campaign.name}
                        </button>
                        {campaign.description && (
                          <div className="text-sm text-muted-foreground truncate max-w-xs">{campaign.description}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariants[campaign.status]}>{statusLabels[campaign.status]}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {campaign.dialingMode ? dialingModeLabels[campaign.dialingMode] : "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {campaign.phoneNumber?.alias || campaign.phoneNumber?.number || "—"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}
                        >
                          <Pencil className="h-3 w-3 mr-1.5" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-500 text-white"
                          onClick={() => navigate(`/power-dialer/${campaign.id}`)}
                        >
                          <Zap className="h-3 w-3 mr-1.5" />
                          Launch
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
